import { useEffect, useRef, useCallback, MutableRefObject } from 'react'
import Hammer from 'hammerjs'
import { useAppStore } from '@store/appStore'
import { useNavigationSettingsStore } from '@store/navigationSettingsSlice'
import { useNavigationSettings } from '@hooks/useNavigationSettings'
import { useFrameSelection } from '@hooks/useFrameSelection'
import { useFrameAnimation } from '@hooks/useFrameAnimation'
import { getPanMoveAnimationService, getShopCardPreviewAnimationService } from '@services/frameAnimationService'
import { NavigationSettings } from '@/types/NavigationSettings'

export interface GestureConfig {
  sensitivity: 'low' | 'medium' | 'high'
  enablePan: boolean
  enablePinch: boolean
  imageDimensions?: { displayWidth: number; displayHeight: number }
  navigationSettings?: NavigationSettings
  onPanEnd?: (timeMs: number) => void  // Callback to notify parent of pan-end
  onFrameShow?: (frameIndex: number) => void  // 🆕 Callback to bring frame to top during animation
}

/**
 * NEW MODE - Simplified gesture hook
 * 
 * Only handles:
 * - Pan: Update viewport world position
 * - Pinch/Zoom: Update zoom scale (only if frame is selected)
 * 
 * NO frame selection, NO coverage calculation, NO direction detection
 */
export function useNewModeGestures(
  elementRef: MutableRefObject<HTMLElement | null>,
  config: GestureConfig = {
    sensitivity: 'medium',
    enablePan: true,
    enablePinch: true,
  }
) {
  // Mobile device detection for zoom limits
  const isTouchDevice = () => ('ontouchstart' in window) || (navigator as any).maxTouchPoints > 0
  
  const hammerRef = useRef<HammerManager | null>(null)
  const isPanningRef = useRef<boolean>(false)
  
  // Use centralized frame selection hook
  const { selectFrame } = useFrameSelection()
  
  // Return isPanningRef so components can check panning state
  useEffect(() => {
    // Expose isPanningRef on hook return for external access
  }, [isPanningRef])
  const isPinchingRef = useRef<boolean>(false)
  const panStartOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const viewportStartPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  // Initialize with current zoom from store (preserves zoom state)
  const initialZoomScale = useRef<number>(useAppStore.getState().zoomScale)
  // Track current zoom locally to avoid stale state reads during rapid wheel events
  const currentZoomRef = useRef<number>(useAppStore.getState().zoomScale)
  const pinchCenterRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 }) // Lock pinch center at start
  const isPinchCenterLockedRef = useRef<boolean>(false) // Flag to track if center is locked
  const isAutoReturningRef = useRef<boolean>(false)
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const configRef = useRef<GestureConfig>(config)

  // Track consecutive auto-return attempts for progressive sticky weakening
  const consecutiveAutoReturns = useRef<number>(0)
  const lastAutoReturnFrame = useRef<number | null>(null)
  const lastAutoReturnTime = useRef<number>(0)

  // 🆕 Track anchor frame center for directional frame selection during pan-move
  // This is the center of the frame when pan started or when frame changed
  const anchorFrameCenterRef = useRef<{ x: number; y: number } | null>(null)

  // 🆕 Track pan start position for accumulated distance calculation
  // This prevents oscillation by ensuring we've panned far enough from the frame selection point
  const panStartRedDotPositionRef = useRef<{ x: number; y: number } | null>(null)

  // 🆕 Animation service and hook - use PAN-MOVE specific service (separate from pan-end)
  const animationService = useRef(getPanMoveAnimationService())
  const { startAnimation: startFrameAnimation, resetAnimation, animatingFrameIndex, animationTrigger } = useFrameAnimation(animationService.current)

  // Store actions
  const setPanOffset = useAppStore((state) => state.setPanOffset)

  // Get navigation settings directly from store to avoid stale config prop
  const { settings: navigationSettings } = useNavigationSettings()

  // Log when navigationSettings changes
  useEffect(() => {
    console.log(`[useNewModeGestures] navigationSettings updated:`, navigationSettings)
  }, [navigationSettings])

  // 🎬 Cancel pan-move animation when setting is disabled
  useEffect(() => {
    if (!navigationSettings.enablePanMoveAnimation) {
      // Cancel any ongoing pan-move preview animation
      if (animationTrigger === 'PAN_MOVE_PREVIEW') {
        console.log(`🎬 [useNewModeGestures] Pan-move animation disabled - cancelling ongoing animation`)
        resetAnimation()
      }
    }
  }, [navigationSettings.enablePanMoveAnimation, animationTrigger, resetAnimation])

  // Check for auto-return after zoom operations (simplified)
  const checkZoomAutoReturn = useCallback(() => {
    if (isAutoReturningRef.current) return

    const state = useAppStore.getState()
    const zoomScale = state.zoomScale
    const currentViewport = state.viewportWorldPosition
    const currentSelectedFrameIndex = state.manuallySelectedFrameIndex

    if (currentSelectedFrameIndex === null || currentSelectedFrameIndex === undefined) return

    // Calculate current red dot position
    const screenCenterX = state.viewportSize.width / 2
    const screenCenterY = state.viewportSize.height / 2
    const currentRedDotWorldX = currentViewport.x + screenCenterX / zoomScale
    const currentRedDotWorldY = currentViewport.y + screenCenterY / zoomScale

    // Check if red dot is within the CURRENTLY selected frame bounds
    let isWithinCurrentFrame = false
    if (configRef.current.imageDimensions) {
      const imageDims = configRef.current.imageDimensions
      const halfWidth = imageDims.displayWidth / 2
      const halfHeight = imageDims.displayHeight / 2

      const currentFrame = state.frameWorldPositions.find(f => f.frameIndex === currentSelectedFrameIndex)
      if (currentFrame) {
        const bboxLeft = currentFrame.worldX - halfWidth
        const bboxRight = currentFrame.worldX + halfWidth
        const bboxTop = currentFrame.worldY - halfHeight
        const bboxBottom = currentFrame.worldY + halfHeight

        isWithinCurrentFrame = currentRedDotWorldX >= bboxLeft && currentRedDotWorldX <= bboxRight &&
            currentRedDotWorldY >= bboxTop && currentRedDotWorldY <= bboxBottom
      }
    }

    // Check if frame is significantly larger than viewport (zoomed in)
    // Skip auto-return if frame is >150% of viewport size - user is exploring a large frame
    const imageDims = configRef.current.imageDimensions
    let isFrameLargerThanViewport = false
    if (imageDims) {
      const frameScreenWidth = imageDims.displayWidth * zoomScale
      const frameScreenHeight = imageDims.displayHeight * zoomScale
      const threshold = 1.5 // 150% of viewport
      isFrameLargerThanViewport = 
        frameScreenWidth > state.viewportSize.width * threshold || 
        frameScreenHeight > state.viewportSize.height * threshold
    }

    if (!isWithinCurrentFrame && useNavigationSettingsStore.getState().settings.enableAutoReturn && !isFrameLargerThanViewport) {
      // Red dot outside frame - center it on selected frame (only if auto-return enabled and frame not zoomed)
      const currentFrame = state.frameWorldPositions.find(f => f.frameIndex === currentSelectedFrameIndex)
      if (currentFrame) {
        const targetViewportX = currentFrame.worldX - screenCenterX / zoomScale
        const targetViewportY = currentFrame.worldY - screenCenterY / zoomScale
        performAutoReturnToPosition(targetViewportX, targetViewportY)
      }
    }
  }, [])

  // Rubber band auto-return animation (simplified)
  const performAutoReturnToPosition = useCallback((targetViewportX: number, targetViewportY: number) => {
    if (isAutoReturningRef.current) return

    isAutoReturningRef.current = true
    const state = useAppStore.getState()
    const currentViewport = state.viewportWorldPosition

    const deltaX = targetViewportX - currentViewport.x
    const deltaY = targetViewportY - currentViewport.y
    const duration = 300 // Fast 300ms animation

    const startTime = Date.now()
    const startViewportX = currentViewport.x
    const startViewportY = currentViewport.y

    const animate = () => {
      const elapsed = Date.now() - startTime
      const t = Math.min(elapsed / duration, 1)

      // Simple easing
      const eased = 1 - Math.pow(1 - t, 3)

      const currentX = startViewportX + deltaX * eased
      const currentY = startViewportY + deltaY * eased

      state.setViewportPosition(currentX, currentY)

      if (t < 1 && isAutoReturningRef.current) {
        requestAnimationFrame(animate)
      } else {
        if (isAutoReturningRef.current) {
          state.setViewportPosition(targetViewportX, targetViewportY)
        }
        isAutoReturningRef.current = false
      }
    }

    requestAnimationFrame(animate)
  }, [])

  // Update config ref
  useEffect(() => {
    configRef.current = config
  }, [config])

  // Initialize DirectHitStrategy when navigation settings are available
  // Subscribe to zoom changes to keep ref updated
  const storeZoomScale = useAppStore((state) => state.zoomScale)
  useEffect(() => {
    currentZoomRef.current = storeZoomScale
  }, [storeZoomScale])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // ===== WHEEL ZOOM (Mouse wheel) =====
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      
      // 🎬 Cancel ALL animations on wheel
      const appState = useAppStore.getState()
      if (appState.isFrameSequenceAnimating) {
        console.log(`[Gesture] wheel - Cancelling frame sequence animation`)
        appState.cancelFrameSequenceAnimation()
      }
      // Also cancel shop preview animation that might be running
      getShopCardPreviewAnimationService().cancel()

      // Cancel auto-return animation if user continues zooming
      if (isAutoReturningRef.current) {
        isAutoReturningRef.current = false
      }

      const state = useAppStore.getState()
      const setZoomAndViewport = state.setZoomAndViewport

      const oldScale = currentZoomRef.current
      const zoomDelta = event.deltaY > 0 ? 0.9 : 1.1
      const newScale = oldScale * zoomDelta
      const minZoom = isTouchDevice() && configRef.current.imageDimensions
        ? Math.max(window.innerWidth / configRef.current.imageDimensions.displayWidth,
                   window.innerHeight / configRef.current.imageDimensions.displayHeight)
        : 0.01
      const clampedScale = Math.max(minZoom, newScale)

      const element = elementRef.current
      if (!element) return
      
      // Calculate red dot position in world space
      const screenCenterX = state.viewportSize.width / 2
      const screenCenterY = state.viewportSize.height / 2
      const redDotWorldX = state.viewportWorldPosition.x + screenCenterX / oldScale
      const redDotWorldY = state.viewportWorldPosition.y + screenCenterY / oldScale

      // 🎯 SMART ZOOM: Check if red dot is over selected frame
      let isRedDotOverFrame = false
      if (configRef.current.imageDimensions && state.manuallySelectedFrameIndex !== null) {
        const imageDims = configRef.current.imageDimensions
        const halfWidth = imageDims.displayWidth / 2
        const halfHeight = imageDims.displayHeight / 2

        const currentFrame = state.frameWorldPositions.find(f => f.frameIndex === state.manuallySelectedFrameIndex)
        if (currentFrame) {
          const bboxLeft = currentFrame.worldX - halfWidth
          const bboxRight = currentFrame.worldX + halfWidth
          const bboxTop = currentFrame.worldY - halfHeight
          const bboxBottom = currentFrame.worldY + halfHeight

          isRedDotOverFrame = redDotWorldX >= bboxLeft && redDotWorldX <= bboxRight &&
                             redDotWorldY >= bboxTop && redDotWorldY <= bboxBottom
        }
      }

      let newViewportX: number
      let newViewportY: number
      
      if (!isRedDotOverFrame && state.manuallySelectedFrameIndex !== null) {
        // Red dot NOT over frame → Center frame on red dot, THEN zoom
        const currentFrame = state.frameWorldPositions.find(f => f.frameIndex === state.manuallySelectedFrameIndex)
        if (currentFrame) {
          newViewportX = currentFrame.worldX - screenCenterX / clampedScale
          newViewportY = currentFrame.worldY - screenCenterY / clampedScale
        } else {
          // Fallback: keep red dot centered
          newViewportX = redDotWorldX - screenCenterX / clampedScale
          newViewportY = redDotWorldY - screenCenterY / clampedScale
        }
      } else {
        // Red dot IS over frame → Zoom to mouse position
        const rect = element.getBoundingClientRect()
        const mouseScreenX = event.clientX - rect.left
        const mouseScreenY = event.clientY - rect.top
        
        const offsetX = -state.viewportWorldPosition.x * oldScale
        const offsetY = -state.viewportWorldPosition.y * oldScale
        
        const worldUnderMouseX = (mouseScreenX - offsetX) / oldScale
        const worldUnderMouseY = (mouseScreenY - offsetY) / oldScale

        newViewportX = worldUnderMouseX - mouseScreenX / clampedScale
        newViewportY = worldUnderMouseY - mouseScreenY / clampedScale
      }

      currentZoomRef.current = clampedScale
      setZoomAndViewport(clampedScale, newViewportX, newViewportY)

      // Check if auto-return needed after zoom settles
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
      }
      zoomTimeoutRef.current = setTimeout(() => {
        checkZoomAutoReturn()
      }, 300)
    }

    element.addEventListener('wheel', handleWheel, { passive: false })

    // Create Hammer instance
    const hammer = new Hammer.Manager(element, {
      touchAction: 'none',
      recognizers: []
    })

    // console.log('🔨 [NewModeGestures] HammerJS initialized')

    hammerRef.current = hammer

    // Declare recognizers for cross-referencing
    let pinch: any = null
    let pan: any = null
    let singleTap: any = null
    let doubleTapRecognizer: any = null

    // Add double-tap recognizer for direct camera highlighting (fallback)
    if (configRef.current.enablePan) { // Double-tap works with pan enabled
      doubleTapRecognizer = new Hammer.Tap({
        event: 'doubletap',
        taps: 2,
        interval: 300, // Max time between taps (ms)
        time: 250,     // Max time for a tap
        threshold: 10, // Max distance for a tap
        posThreshold: 30 // Max distance between taps
      })
      hammer.add(doubleTapRecognizer)

      hammer.on('doubletap', (event: HammerInput) => {
        console.log(`🔄 [DOUBLE TAP DETECTED]`, {
          center: event.center,
          target: event.target?.tagName,
          timeStamp: event.timeStamp
        })

        // Create and dispatch a synthetic double-click event
        const doubleClickEvent = new MouseEvent('dblclick', {
          bubbles: true,
          cancelable: true,
          clientX: event.center.x,
          clientY: event.center.y,
          screenX: event.center.x,
          screenY: event.center.y,
          button: 0,
          buttons: 0
        })

        // Dispatch the event on the target element (should be the selected frame)
        event.target?.dispatchEvent(doubleClickEvent)
        console.log(`🎯 [DOUBLE TAP] Dispatched dblclick event on ${event.target?.tagName}`)
      })
    }

    // Add single-tap recognizer for camera discovery (requires double-tap to fail)
    if (configRef.current.enablePan) { // Single-tap works with pan enabled
      singleTap = new Hammer.Tap({
        event: 'singletap',
        taps: 1,
        interval: 300, // Max time between taps (ms)
        time: 250,     // Max time for a tap
        threshold: 10, // Max distance for a tap
        posThreshold: 30 // Max distance between taps
      })
      hammer.add(singleTap)

      // Single tap only fires if double tap doesn't occur
      if (doubleTapRecognizer) {
        singleTap.requireFailure(doubleTapRecognizer)
      }

      hammer.on('singletap', (event: HammerInput) => {
        // Ignore tap if knob was just being dragged
        const isDragging = document.documentElement.getAttribute('data-knob-dragging')
        console.log(`🔍 [SINGLE TAP] Checking knob-dragging attribute: "${isDragging}"`)
        
        if (isDragging === 'true') {
          console.log(`🚫 [SINGLE TAP BLOCKED] Knob drag in progress, ignoring tap`)
          return
        }
        
        console.log(`🔄 [SINGLE TAP DETECTED]`, {
          center: event.center,
          target: event.target?.tagName,
          timeStamp: event.timeStamp
        })

        // Create and dispatch a synthetic click event for React onClick handler
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: event.center.x,
          clientY: event.center.y,
          screenX: event.center.x,
          screenY: event.center.y,
          button: 0,
          buttons: 0
        })

        // Dispatch the event on the target element (should be the selected frame)
        // Skip if the tap was on a UI control that already handled the real touch event
        // (e.g. overflow trigger button — re-dispatching would toggle it closed)
        const isUiControl = (event.target as HTMLElement)?.closest?.(
          '[data-tap-overflow], button, [role="button"]'
        )
        if (isUiControl) {
          console.log(`🚫 [SINGLE TAP] Skipping synthetic click — target is a UI control`)
          return
        }

        event.target?.dispatchEvent(clickEvent)
      })
    }
    if (configRef.current.enablePinch) {
      pinch = new Hammer.Pinch({
        threshold: 0,
        pointers: 2,
        enable: true
      })
      hammer.add(pinch)

      hammer.on('pinchstart', () => {
        // 🎯 IGNORE PINCH if knob is being dragged
        if (document.documentElement.getAttribute('data-knob-dragging') === 'true') {
          console.log('[Gesture] pinchstart ignored - knob dragging in progress')
          return
        }
        
        // 🎬 Cancel ALL animations on any gesture
        const appState = useAppStore.getState()
        if (appState.isFrameSequenceAnimating) {
          console.log(`[Gesture] pinchstart - Cancelling frame sequence animation`)
          appState.cancelFrameSequenceAnimation()
        }
        // Also cancel shop preview animation that might be running
        getShopCardPreviewAnimationService().cancel()
        
        // Clear any pending wheel zoom timeout
        if (zoomTimeoutRef.current) {
          clearTimeout(zoomTimeoutRef.current)
          zoomTimeoutRef.current = null
        }

        isPinchingRef.current = true
        isPinchCenterLockedRef.current = false
        const currentZoom = useAppStore.getState().zoomScale
        initialZoomScale.current = currentZoom
        currentZoomRef.current = currentZoom
      })

      hammer.on('pinchmove', (event: HammerInput) => {
        // 🎬 Cancel ALL animations on any gesture move
        const appState = useAppStore.getState()
        if (appState.isFrameSequenceAnimating) {
          console.log(`[Gesture] pinchmove - Cancelling frame sequence animation`)
          appState.cancelFrameSequenceAnimation()
        }
        // Also cancel shop preview animation that might be running
        getShopCardPreviewAnimationService().cancel()
        
        // Cancel auto-return animation if user continues pinching
        if (isAutoReturningRef.current) {
          isAutoReturningRef.current = false
        }

        if (!isPinchingRef.current) return

        const state = useAppStore.getState()
        const setZoomAndViewport = state.setZoomAndViewport

        const currentZoom = state.zoomScale
        const newScale = initialZoomScale.current * event.scale
        const minZoom = isTouchDevice() && configRef.current.imageDimensions
          ? Math.max(window.innerWidth / configRef.current.imageDimensions.displayWidth,
                     window.innerHeight / configRef.current.imageDimensions.displayHeight)
          : 0.01
        const clampedScale = Math.max(minZoom, newScale)

        const element = elementRef.current
        if (!element) return
        
        const rect = element.getBoundingClientRect()
        const currentPinchCenterX = event.center.x - rect.left
        const currentPinchCenterY = event.center.y - rect.top

        // Lock pinch center on first pinchmove
        if (!isPinchCenterLockedRef.current) {
          pinchCenterRef.current = { x: currentPinchCenterX, y: currentPinchCenterY }
          isPinchCenterLockedRef.current = true
        }

        const pinchCenterX = pinchCenterRef.current.x
        const pinchCenterY = pinchCenterRef.current.y

        const oldScale = currentZoom
        const oldViewportX = state.viewportWorldPosition.x
        const oldViewportY = state.viewportWorldPosition.y

        const offsetX = -oldViewportX * oldScale
        const offsetY = -oldViewportY * oldScale

        const worldUnderPinchX = (pinchCenterX - offsetX) / oldScale
        const worldUnderPinchY = (pinchCenterY - offsetY) / oldScale

        const newViewportX = worldUnderPinchX - pinchCenterX / clampedScale
        const newViewportY = worldUnderPinchY - pinchCenterY / clampedScale

        currentZoomRef.current = clampedScale
        setZoomAndViewport(clampedScale, newViewportX, newViewportY)
      })

      hammer.on('pinchend', () => {
        isPinchingRef.current = false
        isPinchCenterLockedRef.current = false
        pinchCenterRef.current = { x: 0, y: 0 }

        // Check for auto-return after pinch zoom
        setTimeout(() => {
          checkZoomAutoReturn()
        }, 100)
      })
    }

    // Add pan recognizer
    if (configRef.current.enablePan) {
      pan = new Hammer.Pan({
        direction: Hammer.DIRECTION_ALL,
        threshold: 10,
        pointers: 1,
        enable: true
      })
      hammer.add(pan)

      // Allow simultaneous recognition with proper sequencing
      if (pinch) {
        pan.recognizeWith(pinch)
        pinch.recognizeWith(pan)
      }

      hammer.on('panstart', () => {
        // 🎯 IGNORE PAN if knob is being dragged
        if (document.documentElement.getAttribute('data-knob-dragging') === 'true') {
          console.log('[Gesture] panstart ignored - knob dragging in progress')
          return
        }
        
        // 🎬 Cancel frame sequence animation on any gesture
        const appState = useAppStore.getState()
        if (appState.isFrameSequenceAnimating) {
          console.log(`[Gesture] panstart - Cancelling frame sequence animation`)
          appState.cancelFrameSequenceAnimation()
          return
        }
        
        // Block pan during auto-return animation
        if (isAutoReturningRef.current) {
          return
        }

        // 🆕 Cancel any in-progress frame sequence animation
        resetAnimation()

        if (isPanningRef.current) return

        isPanningRef.current = true

        const state = useAppStore.getState()
        state.setIsPanning(true) // Sync isPanning to store for auto-select guard
        const currentOffset = state.panOffset
        const currentViewport = state.viewportWorldPosition

        panStartOffset.current = { x: currentOffset.x, y: currentOffset.y }
        viewportStartPosition.current = { x: currentViewport.x, y: currentViewport.y }
        
        // 🆕 Calculate current red dot position for anchor initialization
        const zoomScale = state.zoomScale
        const screenCenterX = state.viewportSize.width / 2
        const screenCenterY = state.viewportSize.height / 2
        const currentRedDotWorldX = currentViewport.x + screenCenterX / zoomScale
        const currentRedDotWorldY = currentViewport.y + screenCenterY / zoomScale
        
        // 🆕 Initialize anchor frame center with currently selected frame's center
        const currentFrameIndex = state.manuallySelectedFrameIndex
        if (currentFrameIndex !== null && currentFrameIndex !== undefined) {
          const currentFramePos = state.frameWorldPositions.find(f => f.frameIndex === currentFrameIndex)
          if (currentFramePos) {
            anchorFrameCenterRef.current = {
              x: currentFramePos.worldX,
              y: currentFramePos.worldY
            }
            // 🆕 Also initialize pan start position for accumulated distance tracking
            panStartRedDotPositionRef.current = {
              x: currentRedDotWorldX,
              y: currentRedDotWorldY
            }
            console.log(`[PanStart] Anchor frame center set to Frame ${currentFrameIndex} at (${currentFramePos.worldX.toFixed(1)}, ${currentFramePos.worldY.toFixed(1)})`, `pan start: (${currentRedDotWorldX.toFixed(1)}, ${currentRedDotWorldY.toFixed(1)})`)
          }
        }
      })

      hammer.on('panmove', (event: HammerInput) => {
        // 🎬 Cancel frame sequence animation on any gesture move
        const appState = useAppStore.getState()
        if (appState.isFrameSequenceAnimating) {
          console.log(`[Gesture] panmove - Cancelling frame sequence animation`)
          appState.cancelFrameSequenceAnimation()
          return
        }
        
        // Block pan during auto-return animation
        if (isAutoReturningRef.current) {
          return
        }

        if (!isPanningRef.current) return

        const { deltaX, deltaY } = event
        const state = useAppStore.getState()
        const zoomScale = state.zoomScale

        // Calculate new viewport position
        const proposedViewportX = viewportStartPosition.current.x - (deltaX / zoomScale)
        const proposedViewportY = viewportStartPosition.current.y - (deltaY / zoomScale)

        // Calculate red dot position for frame selection
        const screenCenterX = state.viewportSize.width / 2
        const screenCenterY = state.viewportSize.height / 2
        const currentRedDotWorldX = proposedViewportX + screenCenterX / zoomScale
        const currentRedDotWorldY = proposedViewportY + screenCenterY / zoomScale

        // Update viewport position
        state.setViewportPosition(proposedViewportX, proposedViewportY)

        // Update legacy pan offset
        const newX = panStartOffset.current.x + deltaX
        const newY = panStartOffset.current.y + deltaY
        setPanOffset(newX, newY)

        // 🆕 Calculate accumulated motion vector from anchor frame center
        const panMotion = anchorFrameCenterRef.current ? {
          x: currentRedDotWorldX - anchorFrameCenterRef.current.x,
          y: currentRedDotWorldY - anchorFrameCenterRef.current.y
        } : null

        // 🆕 Calculate accumulated pan distance from pan start position
        // This prevents oscillation by ensuring sufficient total motion since frame selection
        const accumulatedPanDistance = panStartRedDotPositionRef.current ? 
          Math.sqrt(
            Math.pow(currentRedDotWorldX - panStartRedDotPositionRef.current.x, 2) +
            Math.pow(currentRedDotWorldY - panStartRedDotPositionRef.current.y, 2)
          ) : 0

        // Frame selection during pan if enabled
        const viewportSize = useAppStore.getState().viewportSize
        
        // Get current selected frame BEFORE selection
        const selectedFrameBeforeSelection = state.manuallySelectedFrameIndex
        
        // Use centralized selection with 'pan-move' trigger and motion vector
        selectFrame(
          { worldX: currentRedDotWorldX, worldY: currentRedDotWorldY },
          'pan-move',
          configRef.current.imageDimensions || { displayWidth: viewportSize.width, displayHeight: viewportSize.height },
          0,  // consecutiveAutoReturns
          panMotion,  // motion vector from anchor frame center
          accumulatedPanDistance  // 🆕 NEW: accumulated distance to prevent oscillation
        )

        // 🆕 If selected frame changed during pan, update anchor to new frame center
        // This allows motion to accumulate from the new frame's anchor point
        const selectedFrameAfterSelection = useAppStore.getState().manuallySelectedFrameIndex
        if (selectedFrameAfterSelection !== selectedFrameBeforeSelection && 
            selectedFrameAfterSelection !== null && selectedFrameAfterSelection !== undefined) {
          const newFramePos = useAppStore.getState().frameWorldPositions.find(f => f.frameIndex === selectedFrameAfterSelection)
          if (newFramePos) {
            anchorFrameCenterRef.current = {
              x: newFramePos.worldX,
              y: newFramePos.worldY
            }
            // 🆕 Also reset pan start position so accumulated distance resets for new frame
            // This prevents oscillation by requiring new frame to earn its selection distance
            panStartRedDotPositionRef.current = {
              x: currentRedDotWorldX,
              y: currentRedDotWorldY
            }
            console.log(`[PanMove] Frame changed to ${selectedFrameAfterSelection}, anchor updated to (${newFramePos.worldX.toFixed(1)}, ${newFramePos.worldY.toFixed(1)}), pan start reset to (${currentRedDotWorldX.toFixed(1)}, ${currentRedDotWorldY.toFixed(1)})`)

            // 🆕 TRIGGER PAN-MOVE PREVIEW ANIMATION if jumped multiple frames
            // This gives real-time visual feedback during navigation
            // Only if enablePanMoveAnimation is enabled in settings
            const currentSettings = useNavigationSettingsStore.getState().settings
            if (currentSettings.enablePanMoveAnimation && selectedFrameBeforeSelection !== null && selectedFrameBeforeSelection !== undefined) {
              if (selectedFrameAfterSelection > selectedFrameBeforeSelection) {
                // Forward motion - build frames between old and new
                const previewFrames: number[] = []
                for (let i = selectedFrameBeforeSelection + 1; i < selectedFrameAfterSelection; i++) {
                  previewFrames.push(i)
                }
                
                if (previewFrames.length > 0) {
                  console.log(`🎬 [PAN-MOVE PREVIEW] Frame ${selectedFrameBeforeSelection} → ${selectedFrameAfterSelection}, showing ${previewFrames.length} preview frame(s)`)
                  // Start preview animation (yellow, 75ms/frame, every 2nd frame, 60% opacity)
                  startFrameAnimation(
                    previewFrames,
                    selectedFrameAfterSelection,
                    'PAN_MOVE_PREVIEW',
                    () => {} // No-op callback - preview only, don't finalize
                  )
                }
              } else if (selectedFrameAfterSelection < selectedFrameBeforeSelection) {
                // Backward motion - build frames between old and new
                const previewFrames: number[] = []
                for (let i = selectedFrameBeforeSelection - 1; i > selectedFrameAfterSelection; i--) {
                  previewFrames.push(i)
                }
                
                if (previewFrames.length > 0) {
                  console.log(`🎬 [PAN-MOVE PREVIEW] Frame ${selectedFrameBeforeSelection} → ${selectedFrameAfterSelection}, showing ${previewFrames.length} preview frame(s)`)
                  // Start preview animation
                  startFrameAnimation(
                    previewFrames,
                    selectedFrameAfterSelection,
                    'PAN_MOVE_PREVIEW',
                    () => {} // No-op callback - preview only
                  )
                }
              }
            }
          }
        }
      })

      hammer.on('panend', () => {
        // 🎯 IGNORE PANEND if knob is being dragged
        if (document.documentElement.getAttribute('data-knob-dragging') === 'true') {
          console.log('[Gesture] panend ignored - knob dragging in progress')
          return
        }
        
        // 🎬 Cancel frame sequence animation on pan end
        const appState = useAppStore.getState()
        if (appState.isFrameSequenceAnimating) {
          console.log(`[Gesture] panend - Cancelling frame sequence animation`)
          appState.cancelFrameSequenceAnimation()
          return
        }
        
        isPanningRef.current = false
        
        const state = useAppStore.getState()
        state.setIsPanning(false) // Sync isPanning to store
        
        // 🆕 Cancel any in-progress pan-move preview animation before starting pan-end smooth animation
        resetAnimation()
        
        const zoomScale = state.zoomScale
        const currentViewport = state.viewportWorldPosition
        const currentSelectedFrameIndex = state.manuallySelectedFrameIndex

        console.log(`\n🏁 [PAN END] ============================================`)
        console.log(`   Current selected frame: ${currentSelectedFrameIndex}`)

        if (currentSelectedFrameIndex === null || currentSelectedFrameIndex === undefined) {
          console.log(`   ❌ No selected frame - skipping`)
          return
        }

        const screenCenterX = state.viewportSize.width / 2
        const screenCenterY = state.viewportSize.height / 2
        const currentRedDotWorldX = currentViewport.x + screenCenterX / zoomScale
        const currentRedDotWorldY = currentViewport.y + screenCenterY / zoomScale

        console.log(`   Red dot world position: (${currentRedDotWorldX.toFixed(1)}, ${currentRedDotWorldY.toFixed(1)})`)
        console.log(`   Viewport: (${currentViewport.x.toFixed(1)}, ${currentViewport.y.toFixed(1)}), Zoom: ${zoomScale.toFixed(2)}x`)
        console.log(`   Screen center: (${screenCenterX.toFixed(1)}, ${screenCenterY.toFixed(1)})`)

        // Show all frames and distances for debugging
        const allFrameDistances = state.frameWorldPositions.map(fp => {
          const dx = currentRedDotWorldX - fp.worldX
          const dy = currentRedDotWorldY - fp.worldY
          const distance = Math.sqrt(dx * dx + dy * dy)
          const isSelected = fp.frameIndex === currentSelectedFrameIndex
          return { frameIndex: fp.frameIndex, distance, isSelected }
        }).sort((a, b) => a.distance - b.distance)

        console.log(`   📍 Frame distances (nearest 5):`)
        allFrameDistances.slice(0, 5).forEach(f => {
          const marker = f.isSelected ? ' ✓ CURRENT' : f.frameIndex === allFrameDistances[0].frameIndex ? ' ⭐ NEAREST' : ''
          console.log(`      Frame ${f.frameIndex}: ${f.distance.toFixed(1)}px${marker}`)
        })

        // PAN END: Try to select different frame if outside sticky bounds
        const currentSettings = useNavigationSettingsStore.getState().settings
        console.log(`   Settings: enablePanSelection=${currentSettings.enablePanSelection}, useStickyFrameSelection=${currentSettings.useStickyFrameSelection}`)
        
        // Use centralized selection with 'pan-end' trigger
        // 🎬 Animation is now handled INSIDE selectFrame() for jumps > 1
        console.log(`   → Calling selectFrame with pan-end trigger...`)
        const selectionResult = selectFrame(
          { worldX: currentRedDotWorldX, worldY: currentRedDotWorldY },
          'pan-end',
          configRef.current.imageDimensions || { displayWidth: 450, displayHeight: 300 }
        )
        console.log(`   Selection result: ${selectionResult?.selectedFrame !== undefined ? `Frame ${selectionResult.selectedFrame}` : 'null'}`)
        console.log(`   hasChanged: ${selectionResult?.hasChanged}, jumpDistance: ${selectionResult?.jumpDistance || 0}`)
        
        // If pan was blocked (stayed on same frame), trigger arrow animation
        if (selectionResult?.hasChanged === false && currentSelectedFrameIndex !== null) {
          const currentFrame = state.frameWorldPositions.find(f => f.frameIndex === currentSelectedFrameIndex)
          if (currentFrame) {
            const dx = currentRedDotWorldX - currentFrame.worldX
            const dy = currentRedDotWorldY - currentFrame.worldY
            const direction = Math.abs(dx) > Math.abs(dy) 
              ? (dx > 0 ? 'right' : 'left')
              : (dy > 0 ? 'down' : 'up')
            console.log(`🚫 [Pan Blocked] Direction: ${direction}, animating arrow`)
            useAppStore.getState().setPanFailureDirection(direction)
            setTimeout(() => useAppStore.getState().setPanFailureDirection(null), 3000)
          }
        }
        
        // 🎬 Animation is handled by selectFrame() - no need for separate animation here
        // selectFrame() will:
        // 1. For jumps > 1: Animate through intermediate frames, then select final frame
        // 2. For jumps <= 1: Select immediately
        
        // Notify parent that pan-end just happened (for auto-select grace period)
        if (configRef.current.onPanEnd) {
          configRef.current.onPanEnd(Date.now())
        }

        // After selection, get the NEWLY selected frame (might have changed)
        const newlySelectedFrameIndex = useAppStore.getState().manuallySelectedFrameIndex
        console.log(`   After pan-end: frame changed to ${newlySelectedFrameIndex}`)

        // Check if red dot is centered on the newly selected frame
        let isRedDotCentered = false
        const currentFrame = state.frameWorldPositions.find(f => f.frameIndex === newlySelectedFrameIndex)
        
        if (currentFrame) {
          // Calculate distance from red dot to frame center
          const distanceX = Math.abs(currentRedDotWorldX - currentFrame.worldX)
          const distanceY = Math.abs(currentRedDotWorldY - currentFrame.worldY)
          
          // Allow small tolerance (5 pixels in world space)
          const tolerance = 5
          isRedDotCentered = distanceX <= tolerance && distanceY <= tolerance

          // console.log(`   Frame ${currentSelectedFrameIndex} center: (${currentFrame.worldX.toFixed(1)}, ${currentFrame.worldY.toFixed(1)})`)
          // console.log(`   Distance from center: (${distanceX.toFixed(1)}, ${distanceY.toFixed(1)})`)
          // console.log(`   Red dot centered: ${isRedDotCentered}`)
        }

        const autoReturnEnabled = currentSettings.enableAutoReturn
        // console.log(`   Auto-return enabled: ${autoReturnEnabled}`)
        // console.log(`   isAutoReturning: ${isAutoReturningRef.current}`)

        // Check if frame is significantly larger than viewport (zoomed in)
        // Skip auto-return if frame is >150% of viewport size - user is exploring a large frame
        const imageDims = configRef.current.imageDimensions
        let isFrameLargerThanViewport = false
        if (imageDims) {
          const frameScreenWidth = imageDims.displayWidth * zoomScale
          const frameScreenHeight = imageDims.displayHeight * zoomScale
          const threshold = 1.5 // 150% of viewport
          isFrameLargerThanViewport = 
            frameScreenWidth > state.viewportSize.width * threshold || 
            frameScreenHeight > state.viewportSize.height * threshold
          
          // console.log(`   📏 Frame size check: frame=(${frameScreenWidth.toFixed(0)}×${frameScreenHeight.toFixed(0)}), viewport=(${state.viewportSize.width}×${state.viewportSize.height}), threshold=150%, larger=${isFrameLargerThanViewport}`)
        }

        // If red dot not centered on frame, return to frame center (only if auto-return enabled)
        // BUT: Skip auto-return if frame is larger than viewport (user exploring zoomed frame)
        if (!isRedDotCentered && !isAutoReturningRef.current && autoReturnEnabled && currentFrame && !isFrameLargerThanViewport) {
          const targetViewportX = currentFrame.worldX - screenCenterX / zoomScale
          const targetViewportY = currentFrame.worldY - screenCenterY / zoomScale
          console.log(`   📐 Centering newly selected frame ${newlySelectedFrameIndex}...`)
          
          // Track consecutive auto-returns for progressive sticky weakening
          const now = Date.now()
          const timeSinceLastAutoReturn = now - lastAutoReturnTime.current
          const ATTEMPT_TIMEOUT_MS = 3000 // 3 seconds between attempts
          
          if (lastAutoReturnFrame.current === newlySelectedFrameIndex && timeSinceLastAutoReturn < ATTEMPT_TIMEOUT_MS) {
            // Same frame within timeout - increment counter
            consecutiveAutoReturns.current++
          } else {
            // Different frame or timeout expired - reset counter
            consecutiveAutoReturns.current = 0
          }
          
          lastAutoReturnFrame.current = newlySelectedFrameIndex
          lastAutoReturnTime.current = now
          
          console.log(`   ✅ Triggering auto-return to center on frame ${newlySelectedFrameIndex}`)
          performAutoReturnToPosition(targetViewportX, targetViewportY)
        } else {
          const skipReason = isFrameLargerThanViewport ? 'frame larger than viewport (zoomed)' : 
                             isRedDotCentered ? 'already centered' : 
                             isAutoReturningRef.current ? 'already returning' : 
                             !autoReturnEnabled ? 'disabled' : 'unknown'
          console.log(`   ❌ Auto-return NOT triggered - reason: ${skipReason}`)
        }
      })
    }

    return () => {
      element.removeEventListener('wheel', handleWheel)
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
        zoomTimeoutRef.current = null
      }
      hammer.destroy()
    }
  }, [])

  return { isPanningRef, animatingFrameIndex, animationTrigger }
}
